/* eslint-disable @typescript-eslint/no-throw-literal */
import {
  ReactNode,
  Suspense,
  useCallback,
  useDeferredValue,
  useState,
} from 'react';
import { Dashboard } from './dashboard';

declare global {
  interface Window {
    DASH_COURTAIN_TRESHOLD?: number;
  }
}

export class DashboardPanelsBootstraper {
  #hasLoadedAll = false;
  #bootstrappedPanels: Record<string, boolean> = {};
  #hasLoaded: Record<string, boolean> = {};

  #initTimestamp = Date.now();
  #timeoutTreshold = (window.DASH_COURTAIN_TRESHOLD ?? 5) * 1000;

  constructor(public dashboard: Dashboard) {}

  #checkAllHasLoaded = () => {
    if (this.#hasLoadedAll) return true;

    const keys = Object.keys(this.#bootstrappedPanels);
    const hasLoadedAllPanels = !keys.find(
      (current) =>
        !this.#hasLoaded[current] && this.dashboard.getPanel(current).isVisible,
    );
    const hasTimedOutLoading =
      Date.now() - this.#initTimestamp > this.#timeoutTreshold;

    if (hasLoadedAllPanels || hasTimedOutLoading) {
      if (hasTimedOutLoading) {
        const nonBootstrappedPanels = Object.keys(this.#bootstrappedPanels)
          .filter((current) => !this.#hasLoaded[current])
          .map((panelId) => {
            const panelProps = this.dashboard.getPanel(panelId).props;
            return panelProps;
          });

        console.warn(
          `The courtain was removed because of timeout, check that all panels are reporting according to useYieldPanelIsReady's documentation. \n\nIt might have happened that all panels are reporting but the browser was too busy to handle all of them in time, in order to determine what is actually happen you can raise the DASH_COURTAIN_TRESHOLD to a very large number, like 20 for example.`,
          { nonBootstrappedPanels },
        );
      }

      this.#hasLoadedAll = true;
      this.#removeSkeletons();
      return true;
    }

    return false;
  };

  #removeSkeletons = () => {
    const skeleton = document.querySelector(
      '#DashLayoutCourtain',
    ) as HTMLElement;
    const dashContent = document.querySelector(
      '#DashLayoutContent',
    ) as HTMLElement;

    skeleton.style.display = 'none';
    dashContent.style.visibility = 'initial';
    skeleton.remove();
  };

  /**
   * El bootstraper se basa en dos elementos: setHasLoadedInitialData y
   * SuspendPanelUntilDashboardReady.
   *
   * El funcionamiento es muy elemental: Cada panel utiliza el Suspend como
   * wrapper y éste registra el id del panel en un array de paneles pendientes
   * por cargar.
   *
   * Una vez que todos los paneles cargaron, el suspense retoma su tarea y
   * renderiza el panel normalmente.
   */
  setPanelHasLoaded = (panelId: string) => {
    this.#hasLoaded[panelId] = true;
    this.dashboard.getPanel(panelId).setHasLoaded();
  };

  SuspendPanelUntilDashbordReady = ({
    children,
    panelId,
  }: {
    children: ReactNode;
    panelId: string;
  }) => {
    const [hasBootstraped, setHasBootstraped] = useState(false);
    const deferredHasBootstraped = useDeferredValue(hasBootstraped);
    this.#bootstrappedPanels[panelId] = true;

    const LoadWaiter = useCallback(() => {
      if (!this.#checkAllHasLoaded())
        void new Promise<void>((resolve) => {
          const interval = setInterval(() => {
            if (this.#checkAllHasLoaded()) {
              resolve();
              clearInterval(interval);
            }
          }, 300);
        });
      return null;
    }, []);

    if (!deferredHasBootstraped)
      return (
        <div
          ref={(el) => {
            if (el) setHasBootstraped(true);
          }}
        />
      );

    return (
      <Suspense fallback={null}>
        {children}
        <LoadWaiter />
      </Suspense>
    );
  };
}
